import React, { useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { verificationApi } from '../api/verificationApi';
import { profileApi } from '../api/profileApi';
import { Alert, Button, cn } from './ui';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
const MAX_BYTES = 5 * 1024 * 1024;
const COOLDOWN_DAYS = 7;
const COOLDOWN_MS = COOLDOWN_DAYS * 24 * 60 * 60 * 1000;

const AvatarUpload = ({ gradient, subtitle }) => {
  const { user, updateUser } = useAuth();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  const lastChangeMs = user?.avatarChangedAt ? new Date(user.avatarChangedAt).getTime() : 0;
  const elapsedMs = lastChangeMs ? Date.now() - lastChangeMs : Infinity;
  const inCooldown = user?.role !== 'admin' && lastChangeMs > 0 && elapsedMs < COOLDOWN_MS;
  const daysLeft = inCooldown ? Math.ceil((COOLDOWN_MS - elapsedMs) / (24 * 60 * 60 * 1000)) : 0;
  const hasAvatar = Boolean(user?.avatar);
  const letter = (user?.name || '?').charAt(0).toUpperCase();
  const disabled = uploading || inCooldown;

  const handlePickClick = () => {
    if (disabled) return;
    setError('');
    inputRef.current?.click();
  };

  const handleFile = async (file) => {
    setError('');
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) return setError('Profile picture must be a JPG, PNG, WEBP or HEIC image.');
    if (file.size > MAX_BYTES) return setError(`Image too large. Max ${MAX_BYTES / 1024 / 1024} MB.`);

    setUploading(true);
    setProgress(0);
    try {
      const uploadRes = await verificationApi.uploadFile(file, { kind: 'avatar', onProgress: setProgress });
      if (!uploadRes?.url) throw new Error('Upload failed: no URL returned.');
      const saveRes = await profileApi.setAvatar({ url: uploadRes.url });
      updateUser(saveRes.data);
    } catch (err) {
      setError(err.message || 'Failed to update profile picture.');
    } finally {
      setUploading(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (disabled) return;
    setError('');
    setUploading(true);
    try {
      const res = await profileApi.removeAvatar();
      updateUser(res.data);
    } catch (err) {
      setError(err.message || 'Failed to remove profile picture.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:flex-row sm:items-center">
      <button
        type="button"
        onClick={handlePickClick}
        disabled={disabled}
        title={disabled ? (inCooldown ? `Available in ${daysLeft} day${daysLeft === 1 ? '' : 's'}` : 'Uploading…') : 'Click to change'}
        className={cn('group relative flex h-[72px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-full border-0 text-white shadow-sm focus:outline-none focus:ring-4 focus:ring-giggs-green/15', disabled ? 'cursor-not-allowed opacity-75' : 'cursor-pointer')}
        style={{ background: gradient || 'linear-gradient(135deg, #209668, #3CCB7F)' }}
      >
        {hasAvatar ? <img src={user.avatar} alt="Profile" className="h-full w-full object-cover" /> : <span className="font-nunito text-[28px] font-extrabold">{letter}</span>}
        {!disabled && (
          <span className="absolute inset-0 flex items-center justify-center bg-black/45 opacity-0 transition-opacity group-hover:opacity-100 group-focus:opacity-100">
            <svg width="22" height="22" fill="none" stroke="#fff" strokeWidth="2" viewBox="0 0 24 24"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" /><circle cx="12" cy="13" r="4" /></svg>
          </span>
        )}
        {uploading && <span className="absolute inset-0 flex items-center justify-center bg-black/60 font-dm text-[13px] font-bold">{progress}%</span>}
      </button>

      <div className="min-w-0 flex-1">
        <p className="m-0 truncate font-nunito text-base font-extrabold text-giggs-ink">{user?.name || '—'}</p>
        {subtitle && <p className="mb-0 mt-0.5 font-dm text-xs text-giggs-muted">{subtitle}</p>}
        <div className="mt-3 flex flex-wrap gap-2">
          <Button type="button" size="sm" variant="outline" onClick={handlePickClick} disabled={disabled}>
            {uploading ? 'Uploading…' : hasAvatar ? 'Change Photo' : 'Upload Photo'}
          </Button>
          {hasAvatar && !uploading && <Button type="button" size="sm" variant="ghost" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={handleRemove} disabled={disabled}>Remove</Button>}
        </div>
        {inCooldown && <p className="mt-2 mb-0 flex items-center gap-1.5 font-dm text-[11px] text-giggs-muted"><span>◷</span> Available again in {daysLeft} day{daysLeft === 1 ? '' : 's'}</p>}
        {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
      </div>

      <input ref={inputRef} type="file" accept={ALLOWED_IMAGE_TYPES.join(',')} onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} className="hidden" />
    </div>
  );
};

export default AvatarUpload;
